import { lazy, Suspense, useEffect } from 'react'
import PlanMenuHeader from './components/plan-menu-header'
import { PlanMenuContainer } from './styled-component'
import { Route, Switch, useLocation, useParams } from 'react-router-dom'
import { useGetProjectDetailsQuery } from '../../react-query/project/query/use-get-project-details-query'
import { AsyncLoaderPopup } from '../../components/async-loader/async-loader-popup'
import { deserializeQueryString } from '../../utils/urlManager'
import { renamePlanOptions, viewTypeOptions } from './constants'
import CreateFolderModalWrapper from './components/create-folder-modal'
import PlanRenameModalWrapper from './components/rename-modals/plan-wrapper'
import PlanFoldersRenameModalWrapper from './components/rename-modals/plan-folders-wrapper'
import MovePlanModal from './components/move-items-modals/move-plan-modal'
import MoveFolderModal from './components/move-items-modals/move-folder-modal'
import PlanMenuList from './components/plan-menu-list'
import { usePlanMenuListViewControl } from '../../hooks/use-plan-menu-list-view-control'
import PlanMenuTileView from './components/plan-menu-tile'
import { useIsMobile } from '../../zustand/browser-store/use-browser-store'
import PlanMenuMobileView from './components/plan-menu-mobile-view'
import { usePlanMenuStore } from '../../zustand/plan-menu/use-plan-menu-store'
import ExportSinglePlanModal from './components/export-plan-modals/export-single-plan'

const Import = lazy(() => import('../Import'))
const ImportRevision = lazy(() => import('../ImportRevision'))

const PlanMenu = () => {
  const { projectId } = useParams()
  const location = useLocation()
  const urlQuery = deserializeQueryString(location.search)
  const typeIsFolder = urlQuery.type === renamePlanOptions.FOLDER
  const { viewType } = usePlanMenuListViewControl()

  const isMobile = useIsMobile()

  const setCurrentProjectId = usePlanMenuStore(
    (state) => state.setCurrentProjectId
  )
  useEffect(() => {
    setCurrentProjectId(projectId)
  }, [projectId, setCurrentProjectId])

  const { data: projectDetails } = useGetProjectDetailsQuery({ projectId })

  // modals shared by mobile and desktop
  const modals = (
    <Suspense fallback={<AsyncLoaderPopup />}>
      <Switch>
        <Route path="/project/:projectId/import/:destinationFolderId?">
          <Suspense fallback={<AsyncLoaderPopup />}>
            <Import />
          </Suspense>
        </Route>
        <Route path="/project/:projectId/importRevision/:planId/:option?">
          <Suspense fallback={<AsyncLoaderPopup />}>
            <ImportRevision />
          </Suspense>
        </Route>
        <Route path="/project/:projectId/move/:itemId/:destinationFolderId?">
          <Suspense fallback={<AsyncLoaderPopup />}>
            {urlQuery.type !== renamePlanOptions.PLAN && <MoveFolderModal />}
            {urlQuery.type === renamePlanOptions.PLAN && <MovePlanModal />}
          </Suspense>
        </Route>
        <Route path="/project/:projectId/newfolder/:parentId?">
          <Suspense fallback={<AsyncLoaderPopup />}>
            <CreateFolderModalWrapper />
          </Suspense>
        </Route>
        <Route path="/project/:projectId/rename/:id?">
          <Suspense fallback={<AsyncLoaderPopup />}>
            {typeIsFolder ? (
              <PlanFoldersRenameModalWrapper />
            ) : (
              <PlanRenameModalWrapper />
            )}
          </Suspense>
        </Route>
        <Route path="/project/:projectId/exportPlan/:planId?">
          <Suspense fallback={<AsyncLoaderPopup />}>
            <ExportSinglePlanModal />
          </Suspense>
        </Route>
      </Switch>
    </Suspense>
  )

  // mobile view renders outside PlanMenuContainer on purpose: PlanMenuContainer
  // has no defined height, so a height:100%/overflow-y:auto child inside it
  // never gets its own scroll area (the scroll bubbles up to the app Content
  // wrapper instead). Rendering it as a direct child of Content, same as
  // project-menu does for its mobile view, gives it a real height to scroll in.
  if (isMobile) {
    return (
      <>
        {modals}
        <PlanMenuMobileView />
      </>
    )
  }

  return (
    <PlanMenuContainer>
      {modals}

      {/* Plan menu view */}
      <PlanMenuHeader projectDetails={projectDetails} />
      {viewType !== viewTypeOptions.TILE_VIEW && <PlanMenuList />}
      {viewType === viewTypeOptions.TILE_VIEW && <PlanMenuTileView />}
    </PlanMenuContainer>
  )
}

export default PlanMenu
